import { StanDropdown, StanInput, StanTooltip } from '@/components';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import type { DelayUnit, DelayUnitOption } from '../../emails/types/email.types';
import { getMaxDelayForUnit, MAX_DAYS, MAX_HOURS, MAX_MINUTES } from '../../validations/sharedEmailValidations';

interface EmailTimingSectionProps {
    delay: string;
    delayUnit: DelayUnit;
    isCartAbandonedFlow: boolean;
    triggerEventName: string;
    disabled: boolean;
    onChange: (delay: string, unit: DelayUnit) => void;
    error?: string;
}

const DELAY_UNIT_OPTIONS: DropdownMenuOption[] = [
    { label: 'Minutes', value: 'minutes' },
    { label: 'Hours', value: 'hours' },
    { label: 'Days', value: 'days' },
];

export const EmailTimingSection = ({
    delay,
    delayUnit,
    isCartAbandonedFlow,
    triggerEventName,
    disabled,
    onChange,
    error,
}: EmailTimingSectionProps) => {
    const handleDelayChange = (value: string) => {
        // Allow empty string for clearing the field
        if (value === '') {
            onChange('', delayUnit);
            return;
        }

        // Only allow numeric input
        if (!/^\d+$/.test(value)) {
            return;
        }

        // Cap values based on the selected unit
        const maxDelay = getMaxDelayForUnit(delayUnit);
        const numValue = parseInt(value, 10);
        if (numValue > maxDelay) {
            onChange(maxDelay.toString(), delayUnit);
            return;
        }

        onChange(value, delayUnit);
    };

    const handleUnitChange = (value: DropdownValue) => {
        const newUnit = value.value as DelayUnit;
        const maxDelay = getMaxDelayForUnit(newUnit);

        // If current delay exceeds the new unit's max, cap it
        if (delay && delay !== '' && delay !== '0') {
            const currentDelay = parseInt(delay, 10);
            if (currentDelay > maxDelay) {
                onChange(maxDelay.toString(), newUnit);
                return;
            }
        }

        onChange(delay, newUnit);
    };

    // For cart abandoned flows, only show days option
    const unitOptions = isCartAbandonedFlow
        ? DELAY_UNIT_OPTIONS.filter(opt => opt.value === 'days')
        : DELAY_UNIT_OPTIONS;

    const selectedUnit: DelayUnitOption = {
        label: unitOptions.find(opt => opt.value === delayUnit)?.label || 'Days',
        value: delayUnit,
    };

    return (
        <div className='flex flex-col gap-4'>
            <div>
                <div className='inline-flex items-center gap-x-0.5'>
                    <StanTooltip
                        content={
                            <div className='text-caption-2-regular flex w-fit flex-col items-start gap-px'>
                                <p className='text-caption-2-bold'>Timing Constraints</p>
                                <p>
                                    <span className='font-medium'>Minutes:</span> Between 1 and {MAX_MINUTES}
                                </p>
                                <p>
                                    <span className='font-medium'>Hours:</span> Between 1 and {MAX_HOURS}
                                </p>
                                <p>
                                    <span className='font-medium'>Days:</span> Between 1 and {MAX_DAYS}
                                </p>
                            </div>
                        }
                        showIcon
                        iconPosition='right'
                    >
                        <label className='text-label-xs-bold'>Timing</label>
                    </StanTooltip>
                </div>
                <div className='flex flex-col gap-1 sm:flex-row sm:flex-nowrap sm:items-center sm:gap-2'>
                    <div className='w-full sm:max-w-40'>
                        <StanInput
                            name='delay'
                            value={delay.toString()}
                            onChange={e => handleDelayChange(e.target.value)}
                            disabled={disabled}
                            error={!!error}
                            placeholder='1'
                        />
                    </div>

                    <div className='w-full sm:max-w-40'>
                        <StanDropdown
                            key={`delay-unit-${delayUnit}`}
                            id='delay-unit'
                            name='delayUnit'
                            options={unitOptions}
                            currentValue={selectedUnit}
                            onSelect={handleUnitChange}
                            disabled={disabled || isCartAbandonedFlow}
                            preventDeselect
                        />
                    </div>
                    {!error && (
                        <span className='text-gray text-label-sm-regular sm:inline'>after {triggerEventName}</span>
                    )}
                </div>
                <p className='text-caption-2-regular text-negative-active font-semibold'>{error}</p>
            </div>
        </div>
    );
};
